import { RefreshResponseSchema } from "@/schemas";
import { REFRESH_COOLDOWN_MS } from "@/constants";
import { env } from "@/utils";
import notify from "@/utils/notify";

export const BASE_URL = env.VITE_BASE_URL;

let refreshPromise: Promise<boolean> | null = null;
let lastRefreshAt = 0;

export class ApiError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly body?: unknown;
  constructor(
    message: string,
    opts?: { status?: number; code?: string; body?: unknown },
  ) {
    super(message);
    Object.setPrototypeOf(this, ApiError.prototype);
    this.name = "ApiError";
    this.status = opts?.status;
    this.code = opts?.code;
    this.body = opts?.body;
  }
}

export const isApiError = (err: unknown): err is ApiError => {
  return err instanceof ApiError;
};

/**
 * Window event fired whenever an API call comes back HTTP 402
 * `{ code: "BILLING_BLOCKED" }` (employer subscription unpaid / canceled).
 * The app bootstrap (main.tsx) listens for it — only when subscription billing
 * is enabled — and sends the owner to the billing resolution page.
 */
export const BILLING_BLOCKED_EVENT = "pm:billing-blocked";

export function isBillingBlockedError(err: unknown): boolean {
  return isApiError(err) && err.status === 402 && err.code === "BILLING_BLOCKED";
}

function emitIfBillingBlocked(info: { status?: number; code?: string }) {
  if (info.status !== 402 || info.code !== "BILLING_BLOCKED") return;
  try {
    window.dispatchEvent(new CustomEvent(BILLING_BLOCKED_EVENT));
  } catch {
    // non-browser env
  }
}

const MAX_QUERY_RETRIES = 3;

export function shouldRetryQuery(
  failureCount: number,
  error: unknown,
): boolean {
  if (isApiError(error)) {
    const status = error.status;
    if (status === 401 || status === 403 || status === 429) return false;
    if (status !== undefined && status >= 400 && status < 500) return false;
  }
  return failureCount < MAX_QUERY_RETRIES;
}

async function normalizeApiErrorFromResponse(res: Response) {
  let body: unknown = undefined;
  let message = `${res.status} ${res.statusText}`;
  try {
    body = await res.json();
    if (body && typeof body === "object" && (body as any).message) {
      message = (body as any).message;
    }
  } catch {
    try {
      const text = await res.text();
      if (text) message = text;
      body = text;
    } catch {
      // ignore
    }
  }
  const code =
    (body && typeof body === "object" && (body as any).code) || undefined;
  return {
    message,
    code: code as string | undefined,
    status: res.status,
    body,
  };
}

export async function apiRequest<T = unknown>(
  url: string,
  options: RequestInit = {},
  isFormData: boolean = false,
): Promise<T> {
  const buildHeaders = () => ({
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(options.headers as Record<string, string>),
    ...buildAuthHeader(),
  });

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}/${url}`, {
      ...options,
      headers: buildHeaders(),
    });
  } catch (err: unknown) {
    throw new ApiError("Network error", { body: err });
  }

  if (res.status === 401) {
    const refreshed = await performTokenRefresh();
    if (!refreshed) {
      forceLogout();
      const info = await normalizeApiErrorFromResponse(res);
      if (info.status === 403)
        notify.error("You do not have access to this resource.");
      throw new ApiError(info.message, {
        status: info.status,
        code: info.code,
        body: info.body,
      });
    }
    try {
      res = await fetch(`${BASE_URL}/${url}`, {
        ...options,
        headers: buildHeaders(),
      });
    } catch (err: unknown) {
      throw new ApiError("Network error", { body: err });
    }
  }

  if (!res.ok) {
    const info = await normalizeApiErrorFromResponse(res);
    emitIfBillingBlocked(info);
    if (info.status === 403)
      notify.error("You do not have access to this resource.");
    throw new ApiError(info.message, {
      status: info.status,
      code: info.code,
      body: info.body,
    });
  }

  // Read the body ONCE as text. Some endpoints return 204 No Content (empty body),
  // which can't be JSON-parsed — and the stream can't be re-read after a failed
  // res.json(), which is what caused "Failed to parse response" on those routes.
  let text: string;
  try {
    text = await res.text();
  } catch (err: unknown) {
    throw new ApiError("Failed to parse response", { body: err });
  }
  if (!text) return undefined as T; // 204 / empty body
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T; // non-JSON payload
  }
}

export async function performTokenRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  const now = Date.now();
  if (now - lastRefreshAt < REFRESH_COOLDOWN_MS) return Promise.resolve(true);

  refreshPromise = (async () => {
    // Read refresh token from sessionStorage (fallback to legacy localStorage structure)
    let token: string | null = null;
    try {
      token = sessionStorage.getItem("pm-refresh");
    } catch {}
    if (!token) {
      try {
        token =
          JSON.parse(localStorage.getItem("pm-user") || "{}").refreshToken ||
          null;
      } catch {}
    }

    // No stored refresh token → the BE has no way to identify the session.
    // Skip the empty-body POST (which returns 400) and signal failure so the
    // caller force-logs-out cleanly.
    if (!token) return false;

    const refreshUrl = `${BASE_URL}/auth/refresh`;

    try {
      const res = await fetch(refreshUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: token }),
        credentials: "include",
      });
      if (!res.ok) return false;

      const data = await res.json();

      // Support multiple possible shapes returned by the refresh endpoint
      const accessToken =
        data?.accessToken ??
        data?.access_token ??
        data?.data?.accessToken ??
        data?.data?.access_token;
      const refreshToken =
        data?.refreshToken ??
        data?.refresh_token ??
        data?.data?.refreshToken ??
        data?.data?.refresh_token;

      if (accessToken) {
        writeStoredUser({ accessToken });
        try {
          if (refreshToken) sessionStorage.setItem("pm-refresh", refreshToken);
        } catch {}
        return true;
      }

      // Fallback: try zod parsing (older format)
      const parsed = RefreshResponseSchema.safeParse(data);
      if (!parsed.success || !parsed.data.accessToken) {
        return false;
      }
      writeStoredUser({ accessToken: parsed.data.accessToken });
      try {
        const nextRt = parsed.data.refreshToken ?? token;
        if (nextRt) sessionStorage.setItem("pm-refresh", nextRt);
      } catch {}
      return true;
    } catch {
      return false;
    }
  })();

  try {
    const result = await refreshPromise;
    if (result) lastRefreshAt = Date.now();
    return result;
  } finally {
    refreshPromise = null;
  }
}

export function buildAuthHeader(): Record<string, string> {
  try {
    const raw = localStorage.getItem("pm-user");
    if (!raw) return {};
    const user = JSON.parse(raw);
    return user?.accessToken
      ? { Authorization: `Bearer ${user.accessToken}` }
      : {};
  } catch {
    return {};
  }
}

export async function parseOrThrow(res: Response): Promise<never> {
  const info = await normalizeApiErrorFromResponse(res);
  emitIfBillingBlocked(info);
  if (info.status === 403)
    notify.error("You do not have access to this resource.");
  throw new ApiError(info.message, {
    status: info.status,
    code: info.code,
    body: info.body,
  });
}

function forceLogout() {
  try {
    try {
      sessionStorage.removeItem("pm-refresh");
    } catch {}
    localStorage.removeItem("pm-user");
  } catch {}
}

function writeStoredUser(updates: { accessToken?: string }) {
  try {
    const raw = localStorage.getItem("pm-user");
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (updates.accessToken) parsed.accessToken = updates.accessToken;
    localStorage.setItem("pm-user", JSON.stringify(parsed));
  } catch {}
}

export async function apiRequestFormData<T = unknown>(
  url: string,
  formData: FormData,
  options: RequestInit = {},
): Promise<T> {
  const buildHeaders = () => ({
    ...(options.headers as Record<string, string>),
    ...buildAuthHeader(),
  });

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}/${url}`, {
      method: options.method ?? "POST",
      headers: buildHeaders(),
      body: formData,
    });
  } catch (err: unknown) {
    throw new ApiError("Network error", { body: err });
  }

  if (res.status === 401) {
    const refreshed = await performTokenRefresh();
    if (!refreshed) {
      forceLogout();
      const info = await normalizeApiErrorFromResponse(res);
      if (info.status === 403)
        notify.error("You do not have access to this resource.");
      throw new ApiError(info.message, {
        status: info.status,
        code: info.code,
        body: info.body,
      });
    }
    try {
      res = await fetch(`${BASE_URL}/${url}`, {
        method: options.method ?? "POST",
        headers: buildHeaders(),
        body: formData,
      });
    } catch (err: unknown) {
      throw new ApiError("Network error", { body: err });
    }
  }

  if (!res.ok) {
    const info = await normalizeApiErrorFromResponse(res);
    emitIfBillingBlocked(info);
    if (info.status === 403)
      notify.error("You do not have access to this resource.");
    throw new ApiError(info.message, {
      status: info.status,
      code: info.code,
      body: info.body,
    });
  }

  try {
    return (await res.json()) as T;
  } catch (err: unknown) {
    try {
      const text = await res.text();
      return text as unknown as T;
    } catch {
      throw new ApiError("Failed to parse response", { body: err });
    }
  }
}
